import React from 'react';
import { ConfigRow } from '../api/admin';
import { RETRIEVAL_WEIGHT_FIELDS } from '../utils/adminConfigDescriptions';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isSynonymsShape(v: unknown): boolean {
  if (!isPlainObject(v)) return false;
  return Object.values(v).every(
    (x) => Array.isArray(x) && x.every((y) => typeof y === 'string')
  );
}

function isNumericRecord(v: unknown): boolean {
  if (!isPlainObject(v)) return false;
  return Object.values(v).every((x) => typeof x === 'number' && !Number.isNaN(x));
}

/** Single-key `{ text: "..." }` — prompts & responses stored as JSON text. */
function isSingleTextObject(v: unknown): v is { text: string } {
  return isPlainObject(v) && typeof v.text === 'string' && Object.keys(v).length === 1;
}

type Props = { row: ConfigRow };

const ConfigValuePreview: React.FC<Props> = ({ row }) => {
  const { key: configKey, value, category } = row;

  if (isSingleTextObject(value)) {
    return (
      <div className="admin-card-preview-box">
        <span className="admin-card-preview-label">Previzualizare text</span>
        <div className="admin-card-preview admin-card-preview--prose">{value.text}</div>
      </div>
    );
  }

  if (isSynonymsShape(value)) {
    const entries = Object.entries(value as Record<string, string[]>);
    const n = entries.length;
    const show = entries.slice(0, 14);
    const rest = n - show.length;
    return (
      <div className="admin-card-preview-box">
        <span className="admin-card-preview-label">Termeni canonici ({n})</span>
        <div className="admin-card-preview admin-card-preview--map">
          <ul className="admin-preview-map-list">
            {show.map(([term, syns]) => (
              <li key={term} className="admin-preview-map-item">
                <code className="admin-preview-map-term">{term}</code>
                <span className="admin-preview-map-meta">
                  {syns.length} {syns.length === 1 ? 'sinonim' : 'sinonime'}
                </span>
              </li>
            ))}
          </ul>
          {rest > 0 && (
            <p className="admin-preview-map-more">… și încă {rest} termeni</p>
          )}
        </div>
      </div>
    );
  }

  if ((configKey === 'retrieval.weights' || category === 'retrieval') && isNumericRecord(value)) {
    const rec = value as Record<string, number>;
    const keys = Object.keys(rec);
    return (
      <div className="admin-card-preview-box">
        <span className="admin-card-preview-label">Ponderi căutare ({keys.length})</span>
        <div className="admin-card-preview admin-card-preview--weights">
          <dl className="admin-preview-weights-dl">
            {keys.map((k) => {
              const meta = RETRIEVAL_WEIGHT_FIELDS[k];
              return (
                <div key={k} className="admin-preview-weight-row">
                  <dt className="admin-preview-weight-dt">{meta?.label ?? k}</dt>
                  <dd className="admin-preview-weight-dd">{String(rec[k])}</dd>
                </div>
              );
            })}
          </dl>
        </div>
      </div>
    );
  }

  const json = JSON.stringify(value, null, 2);
  return (
    <div className="admin-card-preview-box">
      <span className="admin-card-preview-label">Previzualizare JSON</span>
      <pre className="admin-card-preview admin-card-preview--json">{json}</pre>
    </div>
  );
};

export default ConfigValuePreview;
